'use client';

import './footer.scss';

import { useTranslation } from 'react-i18next';

import { Link } from '@sola/ark-core/atoms/link/link.js';
import { LogoChooser } from '@sola/ark-core/atoms/logos/logos.js';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.js';
import { LogoName } from '@sola/schema/lib/logos.js';
import { FooterProps } from '@sola/schema/lib/skippy.js';
import { submitSubscription } from '@sola/skippy-sdk/requests.js';

import { FormEmailSubscribe } from '../forms/form-email-subscribe/form-email-subscribe';

type CustomizeProps = {
  logo?: LogoName;
  logoStyles?: string;
};

export const Footer: React.FC<FooterProps & CustomizeProps> = ({
  footerMenuPrimary,
  footerMenuSecondary,
  footerMenuSocial,
  footerMenuTrust,
  logo = 'skippy',
  logoStyles,
}) => {
  const { t } = useTranslation();
  return (
    <footer className="FooterSkippy component">
      <div className="container flex flex-col">
        <div className="FooterSkippy__container flex flex-col xl:flex-row xl:items-end justify-between gap-roomy-xs pb-roomy-xs">
          <div className="FooterSkippy__EmailSkeleton w-full">
            <FormEmailSubscribe
              className="max-w-md"
              subscriptionCode="Leads_General_Sign_Up"
              title={t('skippy.form.emailSubscribe.title')}
              description={t('skippy.form.emailSubscribe.description')}
              onSubmit={submitSubscription}
            />
          </div>
          <div className="flex flex-col xl:flex-row xl:items-end justify-between gap-roomy-xs">
            <div className="flex flex-col xl:flex-row gap-roomy-xs justify-end">
              <div className="flex gap-comfy-md items-center">
                {footerMenuTrust?.items
                  ?.filter((ti) => ti.logo)
                  .map((ti) => (
                    <LogoChooser
                      alt={ti.title as string}
                      key={ti.logo}
                      role="img"
                      name={ti.logo!}
                      className="FooterSkippy__trust-icon"
                    />
                  ))}
              </div>
            </div>
          </div>
        </div>
        <div className="FooterSkippy__container grid grid-cols-1 md:grid-cols-4 lg:grid-cols-6 gap-comfy-xl py-roomy-xs">
          {footerMenuPrimary?.items?.map((i) => (
            <div className="flex flex-col gap-comfy-sm" key={i.title}>
              {i.link ? (
                <Link
                  href={i.link}
                  className="FooterSkippy__link label-md label-potent"
                >
                  {i.title}
                </Link>
              ) : (
                <span className="label-md label-potent">{i.title}</span>
              )}
              {i.items?.map((c) =>
                c.link ? (
                  <Link
                    href={c.link}
                    className="FooterSkippy__link label-md"
                    key={c.title}
                  >
                    {c.title}
                  </Link>
                ) : (
                  <span className="label-md">{c.title}</span>
                ),
              )}
            </div>
          ))}
        </div>

        <div className="flex gap-roomy-xs md:flex-row flex-col md:items-start md:justify-between pt-roomy-xs">
          <div className="flex flex-col gap-comfy-sm max-w-xl">
            <LogoChooser
              className={logoStyles}
              name={logo}
              color="white"
              aria-hidden
            />
            <p className="label-md m-0">{t('skippy.footer.copyright')} </p>
            <ul className="FooterSkippy__terms-links flex flex-wrap gap-comfy-xs m-none p-none">
              {footerMenuSecondary?.items?.map((si) => (
                <li key={si.title}>
                  {si.link ? (
                    <Link
                      href={si.link}
                      className="FooterSkippy__link label-md"
                    >
                      {si.title!}
                    </Link>
                  ) : (
                    <span className="label-md">{si.title!}</span>
                  )}
                </li>
              ))}
            </ul>
            <span className="flex flex-col">
              <p className="label-md">{t('skippy.footer.nonProfit')}</p>
              <p className="label-md">{t('skippy.footer.taxNumber')}</p>
            </span>
            <p className="label-md">{t('skippy.footer.trademarks')}</p>
          </div>
          {!!footerMenuSocial?.items?.length && (
            <div className="flex gap-comfy-md items-center">
              {footerMenuSocial?.items
                ?.filter((si) => si.icon)
                .map((si) => (
                  <ArkIcon
                    iconName={si.icon!}
                    key={si.title}
                    className="FooterSkippy__social-icon"
                  />
                ))}
            </div>
          )}
        </div>
      </div>
    </footer>
  );
};
